import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon, MessageCircleIcon } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { WhatsAppIcon } from './WhatsAppIcon';
export function Header() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = {
    en: [{
      name: 'Airport Transfer',
      href: '/ibiza-airport-transfer'
    }, {
      name: 'Villa Transfer',
      href: '/ibiza-villa-transfer'
    }, {
      name: 'Night Driver',
      href: '/ibiza-night-driver'
    }, {
      name: 'Group Transfer',
      href: '/ibiza-group-transfer'
    }, {
      name: 'Reviews',
      href: '/#reviews'
    }],
    es: [{
      name: 'Transfer Aeropuerto',
      href: '/ibiza-airport-transfer'
    }, {
      name: 'Transfer Villa',
      href: '/ibiza-villa-transfer'
    }, {
      name: 'Chófer Nocturno',
      href: '/ibiza-night-driver'
    }, {
      name: 'Transfer Grupo',
      href: '/ibiza-group-transfer'
    }, {
      name: 'Opiniones',
      href: '/#reviews'
    }]
  };
  const links = language === 'en' ? navLinks.en : navLinks.es;
  const whatsappUrl = language === 'en' ? 'https://wa.me/34613756211?text=Hello%2C%20I%20need%20a%20transfer%20in%20Ibiza.%0APickup%3A%0ADestination%3A%0ADate%3A%0ATime%3A%0APassengers%3A' : 'https://wa.me/34613756211?text=Hola%2C%20necesito%20un%20transfer%20en%20Ibiza.%0ARecogida%3A%0ADestino%3A%0AFecha%3A%0AHora%3A%0APasajeros%3A';
  // On SEO pages, header is always solid (no transparent hero overlap)
  const isHomePage = location.pathname === '/';
  const isSolid = isScrolled || isMobileMenuOpen || !isHomePage;
  const headerBg = isSolid ? 'bg-white shadow-sm py-3' : 'bg-transparent py-5';
  const textColor = isSolid ? 'text-navy' : 'text-white';
  const langButtonBase = 'text-xs font-semibold tracking-wider transition-colors duration-300 px-1.5 py-0.5';
  const langActiveClass = isSolid ? 'text-navy' : 'text-white';
  const langInactiveClass = isSolid ? 'text-stone/40 hover:text-stone' : 'text-white/30 hover:text-white/60';
  const isActiveLink = (href: string) => {
    if (href.startsWith('/#')) return false;
    return location.pathname === href;
  };
  return <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${headerBg}`} data-id="element-86">
      <div className="mx-auto max-w-7xl px-5 flex items-center justify-between" data-id="element-87">
        <Link to="/" className={`text-[0.7rem] md:text-[0.8rem] font-heading font-semibold tracking-[0.15em] uppercase transition-colors duration-500 whitespace-nowrap ${textColor}`} data-id="element-88">
          Ibiza Private Drivers
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center space-x-6" data-id="element-89">
          {links.map(link => link.href.startsWith('/#') ? <a key={link.name} href={link.href} className={`text-[0.75rem] font-medium tracking-wide transition-colors duration-300 ${isSolid ? 'text-slate hover:text-navy' : 'text-white/70 hover:text-white'}`} data-id="element-90">
                {link.name}
              </a> : <Link key={link.name} to={link.href} className={`text-[0.75rem] font-medium tracking-wide transition-colors duration-300 ${isActiveLink(link.href) ? isSolid ? 'text-navy font-semibold' : 'text-white font-semibold' : isSolid ? 'text-slate hover:text-navy' : 'text-white/70 hover:text-white'}`} data-id="element-91">
                {link.name}
              </Link>)}
        </nav>

        {/* Desktop Actions */}
        <div className="hidden lg:flex items-center space-x-5" data-id="element-92">
          <div className="flex items-center space-x-1" data-id="element-93">
            <button onClick={() => setLanguage('en')} className={`text-xs font-semibold tracking-wider transition-colors duration-300 px-1.5 py-0.5 ${language === 'en' ? isSolid ? 'text-navy' : 'text-white' : isSolid ? 'text-stone/40 hover:text-stone' : 'text-white/30 hover:text-white/60'}`} data-id="element-94">
              ENG
            </button>
            <span className={`text-xs ${isSolid ? 'text-stone/20' : 'text-white/20'}`} data-id="element-95">
              /
            </span>
            <button onClick={() => setLanguage('es')} className={`text-xs font-semibold tracking-wider transition-colors duration-300 px-1.5 py-0.5 ${language === 'es' ? isSolid ? 'text-navy' : 'text-white' : isSolid ? 'text-stone/40 hover:text-stone' : 'text-white/30 hover:text-white/60'}`} data-id="element-96">
              ESP
            </button>
          </div>

          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 px-4 py-2 rounded-full text-[0.75rem] font-semibold transition-all duration-300 bg-whatsapp hover:bg-whatsapp-hover text-white shadow-sm" data-id="element-97">
            <WhatsAppIcon className="w-3.5 h-3.5" data-id="element-98" />
            <span data-id="element-99">{language === 'en' ? 'Get Price' : 'Pedir Precio'}</span>
          </a>
        </div>

        {/* Mobile Toggle */}
        <div className="flex items-center space-x-4 lg:hidden" data-id="element-100">
          <div className="flex items-center space-x-1" data-id="element-101">
            <button onClick={() => setLanguage('en')} className={`text-xs font-semibold tracking-wider px-1 ${language === 'en' ? textColor : isSolid ? 'text-stone/30' : 'text-white/30'}`} data-id="element-102">
              ENG
            </button>
            <span className={`text-xs ${isSolid ? 'text-stone/20' : 'text-white/20'}`} data-id="element-103">
              /
            </span>
            <button onClick={() => setLanguage('es')} className={`text-xs font-semibold tracking-wider px-1 ${language === 'es' ? textColor : isSolid ? 'text-stone/30' : 'text-white/30'}`} data-id="element-104">
              ESP
            </button>
          </div>
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className={`p-1 transition-colors ${textColor}`} aria-label="Toggle menu" data-id="element-105">
            {isMobileMenuOpen ? <XIcon className="w-5 h-5" data-id="element-106" /> : <MenuIcon className="w-5 h-5" data-id="element-107" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-100 shadow-lg py-5 px-5 flex flex-col space-y-4" data-id="element-108">
          {links.map(link => link.href.startsWith('/#') ? <a key={link.name} href={link.href} onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-slate hover:text-navy py-1.5 tracking-wide" data-id="element-109">
                {link.name}
              </a> : <Link key={link.name} to={link.href} onClick={() => setIsMobileMenuOpen(false)} className={`text-sm font-medium py-1.5 tracking-wide ${isActiveLink(link.href) ? 'text-navy font-semibold' : 'text-slate hover:text-navy'}`} data-id="element-110">
                {link.name}
              </Link>)}
          <div className="pt-4 border-t border-gray-100" data-id="element-111">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center space-x-2 border border-navy/15 text-navy px-5 py-3 rounded-full font-medium w-full transition-colors text-sm tracking-wide" data-id="element-112">
              <WhatsAppIcon className="w-4 h-4 text-whatsapp" data-id="element-113" />
              <span data-id="element-114">
                {language === 'en' ? 'Get your price on WhatsApp' : 'Pedir precio por WhatsApp'}
              </span>
            </a>
          </div>
        </div>}
    </header>;
}